import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { inject, Service } from '@angular/core';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { StringService } from '@portal-core/general/services/string.service';
import { ProcessAttemptStatus } from '@portal-core/processes/enums/process-attempt-status.enum';
import { ProcessPublicationType } from '@portal-core/publications/enums/process-publication-type.enum';
import { Publication } from '@portal-core/publications/models/publication.model';
import { PublicationsApiService } from '@portal-core/publications/services/publications-api.service';
import { PublicationsDataService } from '@portal-core/publications/services/publications-data.service';
import { AccessMap, AccessRequest } from '@portal-core/data/types/access-request.type';
import { PublicationOperationType } from '@portal-core/publications/types/publication-operation-type.type';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { map, Observable, tap } from 'rxjs';

@Service()
@Resettable()
export class PublicationsService extends CollectionServiceBase<Publication> {
  private readonly publicationApiService = inject(PublicationsApiService);
  private readonly stringService = inject(StringService);
  viewableOutputExtensions = ['htm', 'html', 'pdf'];


  constructor() {
    super(inject(PublicationsDataService));
  }

  protected override fetchItemById$(itemId: number): Observable<Publication> {
    return this.publicationApiService.getPublication$(itemId).pipe(
      map(publication => this.processPublication(publication))
    );
  }

  getAccess$(request: AccessRequest<Publication, PublicationOperationType>): Observable<AccessMap<PublicationOperationType>> {
    return this.publicationApiService.getAccess$(request);
  }

  bulkDeletePublications$(projectId: number, publicationIds: number[]): Observable<MadCloudResult<void>> {
    return this.publicationApiService.bulkDeletePublications$(projectId, publicationIds).pipe(
      tap(() => this.deleteItems$(publicationIds))
    );
  }

  updatePublication$(publication: Publication): Observable<void> {
    this.addItemsToListById$('Targets', publication.Id, [publication]);
    return this.updateItems$([this.processPublication(publication)]);
  }

  getPublicationsPageByProjectId$(projectId: number, filter: PageFilter): Observable<Page<Publication>> {
    return this.publicationApiService.getPublicationsPageByProjectId$(projectId, filter).pipe(
      tap(page => this.processPublicationsPage(page))
    );
  }

  getPublicationsPage$(filter: PageFilter): Observable<Page<Publication>> {
    return this.publicationApiService.getPublicationsPage$(filter).pipe(
      tap(page => this.processPublicationsPage(page))
    );
  }

  processPublicationsPage(page: Page<Publication>): Page<Publication> {
    if (Array.isArray(page?.Items)) {
      this.processPublications(page.Items);
    }
    return page;
  }

  processPublications(publications: Publication[]): Publication[] {
    publications.forEach(publication => this.processPublication(publication));
    return publications;
  }

  processPublication(publication: Publication): Publication {
    if (publication) {
      const status = publication.Process?.LastProcessAttempt?.Status;
      // Treat a pre-pickup-canceled publication (CanceledByUserId set, no attempt yet) as
      // finished — nothing else will happen to it.
      const isFinished = PublicationsService.isTerminalStatus(status) || this.isCanceled(publication);

      publication.CanBeCanceled = !isFinished;
      publication.CanBeDeleted = isFinished;
      publication.IsFinished = isFinished;
      publication.CanDownloadLog = isFinished;
      publication.IsStopped = isFinished;

      if (isFinished) {
        publication.Completed = publication.Process?.LastProcessAttempt?.EndedOn;
      }

      // ProcessAttempt.Progress is [0, 1] (1 forced if Completed); mat-progress-bar
      // wants [0, 100]. Null means no progress event has arrived yet — render as 0.
      const progress = isFinished ? 1 : publication.Process?.LastProcessAttempt?.Progress ?? 0;
      publication.Progress = progress * 100;
    }

    return publication;
  }

  private static isTerminalStatus(status: ProcessAttemptStatus | null): boolean {
    return status === ProcessAttemptStatus.Complete
      || status === ProcessAttemptStatus.Failed
      || status === ProcessAttemptStatus.Canceled
      || status === ProcessAttemptStatus.Aborted;
  }

  /**
   * True when the publication is queued and waiting for the worker. Includes the pre-pickup
   * window where the worker hasn't yet created a ProcessAttempt — the publication has been
   * enqueued but no attempt row exists yet, which is still "queued" from the user's
   * perspective. A pre-pickup canceled publication is excluded so it doesn't show as both.
   */
  isQueued(publication: Publication | null | undefined): boolean {
    const attempt = publication?.Process?.LastProcessAttempt;
    if (!attempt) {
      return !!publication && !publication.CanceledByUserId;
    }
    return attempt.Status === ProcessAttemptStatus.Queued;
  }

  /**
   * True when the publication was canceled. Includes the pre-pickup window where the user
   * cancelled a queued publication before the worker created an attempt — Publication
   * .CanceledByUserId is set but no ProcessAttempt with Status=Canceled exists yet.
   */
  isCanceled(publication: Publication | null | undefined): boolean {
    if (publication?.Process?.LastProcessAttempt?.Status === ProcessAttemptStatus.Canceled) {
      return true;
    }
    return !!publication?.CanceledByUserId && !publication?.Process?.LastProcessAttempt;
  }

  /**
   * Like {@link getColorForState} but handles the pre-pickup queued/canceled cases (when
   * the publication has no ProcessAttempt yet). Prefer this over reaching into
   * `publication.Process.LastProcessAttempt.Status` at call sites.
   */
  getColorForPublication(publication: Publication | null | undefined, paletteShade: '' | 'muted' | 'vivid' = ''): string {
    if (this.isCanceled(publication)) {
      return this.getColorForState(ProcessAttemptStatus.Canceled, paletteShade);
    }
    if (this.isQueued(publication)) {
      return this.getColorForState(ProcessAttemptStatus.Queued, paletteShade);
    }
    return this.getColorForState(publication?.Process?.LastProcessAttempt?.Status ?? null, paletteShade);
  }

  getColorForState(status: ProcessAttemptStatus | null, paletteShade: '' | 'muted' | 'vivid' = ''): string {
    let paletteName: string;
    switch (status) {
      case ProcessAttemptStatus.Complete:
        paletteName = 'build-success';
        break;
      case ProcessAttemptStatus.Failed:
      case ProcessAttemptStatus.Aborted:
        paletteName = 'build-fail';
        break;
      case ProcessAttemptStatus.Canceled:
        paletteName = 'build-cancel';
        break;
      case ProcessAttemptStatus.Queued:
        paletteName = 'info';
        break;
      case ProcessAttemptStatus.Running:
        paletteName = 'build-success';
        break;
      default:
        paletteName = 'build-unknown';
        break;
    }

    return `${paletteName}${paletteShade ? '-' + paletteShade : ''}`;
  }

  /**
   * User-facing label for a publication's current state. See BuildsService.getStatusDisplayText
   * for the shape — same idea, with a publication-specific phase map. Notable wording
   * difference from builds: the source-fetch phase reads "Staging Files" instead of "Getting Files".
   */
  getStatusDisplayText(publication: Publication | null | undefined): string {
    // Handle pre-pickup cases first — isCanceled/isQueued cover both the explicit-status form
    // and the no-attempt-yet form.
    if (this.isCanceled(publication)) {
      return 'Canceled';
    }
    if (this.isQueued(publication)) {
      return 'Queued';
    }

    const attempt = publication?.Process?.LastProcessAttempt;
    switch (attempt?.Status) {
      case ProcessAttemptStatus.Complete:
        return 'Complete';
      case ProcessAttemptStatus.Failed:
        return 'Failed';
      case ProcessAttemptStatus.Aborted:
        return 'Aborted';
      case ProcessAttemptStatus.Running:
        if (!attempt?.LastEvent) {
          return 'Running';
        }
        return PublicationsService.publicationPhaseMap[attempt.LastEvent] ?? this.stringService.sentenceCase(attempt.LastEvent);
      default:
        return '';
    }
  }

  private static readonly publicationPhaseMap: Readonly<Record<string, string>> = {
    // Staging Files (publication-specific wording — builds say "Getting Files")
    gettingSource: 'Staging Files',
    gettingSourceComplete: 'Staging Files',
    publicationProcessing: 'Staging Files',
    // Publishing
    publicationStarted: 'Publishing',
    compilePublishStart: 'Publishing',
    compilePublishComplete: 'Publishing'
  };

  createPublications$(buildId: number, destinationFilePaths: string[]): Observable<Publication[]> {
    return this.publicationApiService.createPublications$(buildId, destinationFilePaths);
  }

  processPublication$(publicationId: number, processPublicationType: ProcessPublicationType): Observable<void> {
    return this.publicationApiService.processPublication$(publicationId, processPublicationType);
  }

  getPublicationLogStreamUrl(publicationId: number): string {
    return this.publicationApiService.getPublicationLogStreamUrl(publicationId);
  }
}
